import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { take } from 'rxjs';
import {
  HttpForbiddenException,
  HttpUnauthorizedException,
} from 'src/app/shared/errors/http/http-errors';
import { NotAuthorizedException } from 'src/app/shared/errors/cognito/cognito-errors';
import { LocalStorageKeys } from 'src/app/shared/models/constants/storage.keys';
import { Representative } from 'src/app/shared/models/dtos/register-form/representative/representative';
import { RepresentativeRole } from 'src/app/shared/models/dtos/register-form/representative/representative-role';
import { User } from 'src/app/shared/models/user/user';
import { UserService } from '../../api/user/user.service';
import { RewardManagementService } from '../reward-management/reward-management.service';
import { TranslationService } from '../translation/translation.service';
import { DialogService } from '../dialog/dialog.service';
import { BannerResponseDTO } from 'src/app/shared/models/dtos/responses/banner-response-dto/banner-response-dto';

@Injectable({
  providedIn: 'root',
})
/**
 * Service used to interact with current authenticated user
 */
export class AuthenticatedUserService {
  private _user?: User;
  private _representative?: Representative;
  representativeDeleted = false;
  representative$ = new BehaviorSubject<Representative | undefined>(undefined);
  errorMsg = {
    failureFetchingTokens: 'failure-fetching-user-tokens',
  };
  private _bannerData?: BannerResponseDTO;
  bannerData$ = new BehaviorSubject<BannerResponseDTO | undefined>(undefined);
  bannerLang = '';
  formatLanguageForBanner: any = {
    es: 'SPANISH',
    'pt-BR': 'PORTUGUESE',
    en: 'ENGLISH',
  };

  /**
   * @constructor
   */
  constructor(
    private userService: UserService,
    private readonly rewardManagementService: RewardManagementService,
    private readonly translationService: TranslationService,
    private readonly dialogService: DialogService
  ) {
    translationService.translateObject(this.errorMsg);
  }

  /**
   * Gets current representative
   */
  get representative() {
    return this._representative;
  }

  /**
   * Sets representative
   * @param {Representative | undefined} value
   */
  set representative(value: Representative | undefined) {
    this._representative = value;
    this.representative$.next(value);
  }

  /**
   * Initializes representative if possible
   */
  initializeRepresentative() {
    if (this.user && !this.representative) {
      this.userService
        .getRepresentativeInfo()
        .pipe(take(1))
        .subscribe({
          next: (representative) => {
            this.representative = representative;
            // TODO
          },
          error: (e) => {
            if (
              e instanceof HttpForbiddenException ||
              e instanceof NotAuthorizedException ||
              e instanceof HttpUnauthorizedException
            ) {
              this.removeUser();
            }
          },
        });
    }
  }

  /**
   * Resets current representative
   */
  resetRepresentative() {
    this.representative = undefined;
    this.initializeRepresentative();
  }

  /**
   * Gets current banner data
   */
  get bannerData() {
    return this._bannerData;
  }

  /**
   * Sets banner data
   * @param {BannerResponseDTO | undefined} value
   */
  set bannerData(value: BannerResponseDTO | undefined) {
    this._bannerData = value;
    this.bannerData$.next(value);
  }

  /**
   * Initializes banner
   */
  initializeBanner() {
    if (!this.bannerData) {
      this.bannerLang =
        this.formatLanguageForBanner[this.translationService.getActiveLang()];
      this.userService.getBannerInfo(this.bannerLang).subscribe((info) => {
        this.bannerData = info;
      });
    }
  }

  /**
   * reset banner data
   */
  resetBannerData() {
    this.bannerData = undefined;
  }

  /**
   * Sets currently logged in user, updating local storage
   * @param {User | undefined} user
   */
  set user(user: User | undefined) {
    if (user) {
      localStorage.setItem(LocalStorageKeys.USER, JSON.stringify(user));
      this._user = user;
    }
  }

  /**
   * Current logged in user instance
   */
  get user() {
    if (!this._user) {
      const localStorageUser = localStorage.getItem(LocalStorageKeys.USER);
      if (localStorageUser) {
        this._user = User.deserialize(User)(localStorageUser);
      }
    }
    return this._user;
  }

  /**
   * Removes stored user
   */
  removeUser() {
    localStorage.removeItem(LocalStorageKeys.USER);
    this._user = undefined;
    this.representative = undefined;
    this.representativeDeleted = false;
    this.dialogService.closeCurrentDialog();
  }

  /**
   * Gets business id from representative info query
   */
  async getBusinessId() {
    if (!this.user) {
      throw new NotAuthorizedException();
    }
    if (!this.representative) {
      const representative$ = this.userService.getRepresentativeInfo();
      const representative = await lastValueFrom(representative$);
      this.representative = representative;
    }
    return this.representative.businessId;
  }

  /**
   * Decides if the user is logged in or not
   * @return {boolean}
   */
  isLoggedIn(): boolean {
    return this.user !== undefined;
  }

  /**
   * @return {boolean}
   */
  isAdmin(): boolean {
    const isAdmin = Boolean(
      this.representative?.role === RepresentativeRole.BUSINESS_ADMIN
    );
    return isAdmin;
  }

  /**
   * Updates currently logged in user tokens
   * @param {string} accessToken
   * @param {string} idToken
   */
  updateTokens(accessToken: string, idToken: string) {
    const setTokens = this.user?.tokens!;
    if (setTokens) {
      setTokens.accessToken = accessToken;
      setTokens.idToken = idToken;
      localStorage.setItem(LocalStorageKeys.USER, JSON.stringify(this.user));
    } else {
      throw new Error(this.errorMsg.failureFetchingTokens);
    }
  }
}
